import type { SyncOrdersResponse } from '../services/b2bSyncApi';
import { buildResultMessage, count, linkedToOtherPaymentsProblems, paymentDiscrepancyProblems } from './reconcileMessages';
import type { ResultMessage } from './reconcileMessages';
import { Order, OrderItem, ProductGroup, SerialSlot, AssignmentStatus } from '../types/orders';

/**
 * Get the order link URL for a given order ID
 */
export function getOrderLink(orderId: number | string): string | null {
  if (!orderId) return null;
  return `https://b2b.naviter.com/wp-admin/post.php?post=${orderId}&action=edit`;
}

/**
 * Format order link text (e.g., "#12345")
 */
export function formatOrderLink(orderId: number | string): string {
  if (!orderId) return '';
  return `#${orderId}`;
}

/**
 * Group order items by product ID
 */
export const groupByProduct = (items: OrderItem[]): ProductGroup[] => {
  const groups = new Map<number, ProductGroup>();

  items.forEach(item => {
    const productId = item.local_product_id;
    
    if (!groups.has(productId)) {
      groups.set(productId, {
        productId,
        productName: item.local_product_name,
        b2bProductName: item.product_name,
        items: [],
        totalQuantity: 0,
        totalAssigned: 0,
        allAssignments: [],
        allPastAssignments: []
      });
    }

    const group = groups.get(productId)!;
    group.items.push(item);
    group.totalQuantity += item.quantity;
    group.totalAssigned += item.serials_assigned;
    group.allAssignments.push(...item.assignments);
    if (item.past_assignments) {
      group.allPastAssignments.push(...item.past_assignments);
    }
  });

  return Array.from(groups.values());
};

/**
 * Generate serial slots for a product group (one slot per remaining serial number needed).
 * An item can hold more devices than its quantity (legacy orders put all devices on one
 * item), so the number of slots is capped by what the group as a whole still needs.
 */
export const generateSerialSlots = (group: ProductGroup): SerialSlot[] => {
  const slots: SerialSlot[] = [];
  let groupRemaining = group.totalQuantity - group.totalAssigned;

  group.items.forEach(item => {
    const remaining = Math.min(item.quantity - item.serials_assigned, groupRemaining);
    groupRemaining -= Math.max(0, remaining);
    for (let i = 0; i < remaining; i++) {
      slots.push({
        slotId: `${group.productId}-${item.id}-${item.serials_assigned + i}`,
        productId: group.productId,
        itemId: item.id,
        slotIndex: item.serials_assigned + i
      });
    }
  });
  
  return slots;
};

/**
 * Get assignment status for an order
 */
export const getAssignmentStatus = (order: Order): AssignmentStatus => {
  const total = order.total_quantity;
  const assigned = order.serials_assigned;
  
  if (total === 0) return { text: 'No items', className: 'text-gray-500' };
  if (assigned === 0) return { text: `0/${total}`, className: 'text-red-600' };
  if (assigned < total) return { text: `${assigned}/${total}`, className: 'text-yellow-600' };
  return { text: `${assigned}/${total} ✓`, className: 'text-green-600' };
};

/**
 * The result of an order sync as one line, for example
 * "27 fetched · 3 new (1 with devices, 2 software-only) · 22 updated · 2 skipped (draft, unpaid, failed or cancelled)".
 * The counts add up to the number fetched. A count of 0 is left out.
 * Failed orders are not in this line; see orderSyncProblems.
 */
export function formatOrderSyncResult(data: SyncOrdersResponse): string {
  const parts = [`${data.total ?? data.synced} fetched`];
  const c = data.counts;
  if (c) {
    const newOrders = c.newDevice + c.newSoftware;
    if (newOrders > 0) parts.push(`${newOrders} new (${c.newDevice} with devices, ${c.newSoftware} software-only)`);
    if (c.updated > 0) parts.push(`${c.updated} updated`);
    if (c.deleted > 0) parts.push(`${c.deleted} deleted (cancelled)`);
    if (c.skipped > 0) parts.push(`${c.skipped} skipped (draft, unpaid, failed or cancelled)`);
  }
  return parts.join(' · ');
}

/**
 * Problems of an order sync, one sentence each: orders that failed to save,
 * the page limit, and a failed linking of payments to the synced orders.
 */
export function orderSyncProblems(data: SyncOrdersResponse): string[] {
  const problems: string[] = [];
  const errors = data.counts?.errors ?? 0;
  if (errors > 0) problems.push(`${count(errors, 'order')} could not be saved. See the server log.`);
  if (data.pageCapped) problems.push('The page limit was reached, so some orders may be missing. Run the sync again.');
  if (data.backfill === null) {
    problems.push('Linking payments to the synced orders failed. See the server log.');
  } else if (data.backfill?.errors?.length) {
    problems.push(`Linking payments to the synced orders failed for: ${data.backfill.errors.join('; ')}.`);
  }
  // Links the sync made to orders that came in with it.
  problems.push(...linkedToOtherPaymentsProblems(data.backfill?.linkedToOtherPayments));
  problems.push(...paymentDiscrepancyProblems(data.backfill?.bankTransferDiscrepancies));
  return problems;
}

/** The order sync result with its problems; a warning when there are problems. */
export function formatOrderSyncMessage(data: SyncOrdersResponse, prefix = ''): ResultMessage {
  return buildResultMessage(`${prefix}${formatOrderSyncResult(data)}`, orderSyncProblems(data));
}
